import { useForm as createUseForm } from "@ant-design-vue/use";
import { Col, Row } from "ant-design-vue";
import lodash from "lodash";
import { defineAsyncComponent, reactive, toRaw } from "vue";
import { addModal, setIsLoading as setIsLoadingApp } from "../../../../app.logic";
import {
    createFieldCheckbox,
    createFieldDate,
    createFieldInput,
    createFieldSelect,
    createFieldTemplateBuilder,
    createFieldTreeSelect
} from "../../../../components/input/template-builder/template-builder.logic";
import { setAppPageHeader } from "../../../../components/layout/app-layout/app-layout.logic";
import { createColumnDatetime, createColumnInput, createColumnSelect } from "../../../../components/table/c-table.logic";
import i18n from "../../../../i18n";
import router from "../../../../router";
import store from "../../../../store";
import {
    createButtonActive,
    createButtonArchive,
    createButtonCopy,
    createButtonCreate,
    createButtonDelete,
    createButtonEdit,
    createButtonSave,
    createButtonView
} from "../../../../utils/button";
import { modalStyleIFrame } from "../../../../utils/constant";
import {
    checkAttribute,
    copyForm,
    initUseForm,
    onClickActive,
    onClickActiveAll,
    onClickArchive,
    onClickArchiveAll,
    onClickCreate,
    onClickDelete,
    onClickDeleteAll,
    onClickEdit,
    onClickView,
    putField
} from "../../../../utils/form";
import { createLoading } from "../../../../utils/loading";
import { has } from "../../../../utils/menu-context";
import {
    createAppPageHeaderTop,
    createManageActive,
    createManageArchive,
    createManageDelete,
    createManageExportCsv,
    createManageExtraCreate,
    createManageSelect,
    createPage,
    createRowActionEdit,
    createRowActionView
} from "../../../../utils/page";
import { required, validateError } from "../../../../utils/validate";

const CIFrame = defineAsyncComponent(() => import("../../../../components/iframe/CIFrame"));

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/master/product";
export const routerPrefix = "products";

export const callSave = ({ form }) => store.dispatch(`${actionPrefix}/save`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });
export const callProductType = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "PRODUCT_TYPE", value: searchQuery, valueEn: searchQuery, valueLogic2: "OR" });
export const callProductConsumption = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "PRODUCT_CONSUMPTION", value: searchQuery, valueEn: searchQuery, valueLogic2: "OR" });
export const callAquaticSource = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "AQUATIC_SOURCE", value: searchQuery, valueEn: searchQuery, valueLogic2: "OR" });
export const callAquaticType = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "AQUATIC_TYPE", value: searchQuery, valueEn: searchQuery, valueLogic2: "OR" });
export const callAquaticSpecies = ({ parentId, searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroupParent", { group: "AQUATIC_SPECIES", parentId, value: searchQuery, valueEn: searchQuery, valueLogic2: "OR" });
export const callProductMethod = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "PRODUCT_METHOD", value: searchQuery, valueEn: searchQuery, valueLogic2: "OR" });
export const callProductFeture = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "PRODUCT_FEATURE", value: searchQuery, valueEn: searchQuery, valueLogic2: "OR" });

export const onSearch = ({ page, isSet = true }) => {
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({ criteria: { ...page, data: [] } })
        .then(page => (isSet ? (state.page = { ...page }) : page))
        .finally(() => setIsLoading(false));
};

export const onClickSave = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true);
            return callSave({ form: data.useForm.modelRef })
                .finally(() => setIsLoadingApp(false))
                .then(record => onClickView({ record, routerPrefix }));
        })
        .catch(error => validateError({ error }));
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createSearchOneCriteria = ({ id, forLog }) => {
    if (!forLog) {
        return {
            select: ["**"],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }]
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    }
};

export const createColumns = () => {
    const columns = [
        createColumnInput({ title: () => i18n.global.t("model.product.code"), dataIndex: "code" }),
        createColumnInput({ title: () => i18n.global.t("model.product.nameTh"), dataIndex: "nameTh" }),
        createColumnInput({ title: () => i18n.global.t("model.product.nameEn"), dataIndex: "nameEn" }),
        createColumnSelect({
            title: () => i18n.global.t("model.product.refProductType"),
            dataIndex: "refProductType.value",
            inputProp: () => ({
                list: callProductType,
                renderLabel: ({ item }) => (
                    <div>
                        {item.value} ({item.valueEn})
                    </div>
                )
            }),
            search: {
                name: "refProductType.id"
            }
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.product.refProductConsumption"),
            dataIndex: "refProductConsumption.value",
            inputProp: () => ({
                list: callProductConsumption,
                renderLabel: ({ item }) => (
                    <div>
                        {item.value} ({item.valueEn})
                    </div>
                )
            }),
            search: {
                name: "refProductConsumption.id"
            }
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.product.refAquaticSource"),
            dataIndex: "refAquaticSource.value",
            inputProp: () => ({
                list: callAquaticSource,
                renderLabel: ({ item }) => (
                    <div>
                        {item.value} ({item.valueEn})
                    </div>
                )
            }),
            search: {
                name: "refAquaticSource.id"
            }
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.product.refAquaticType"),
            dataIndex: "refAquaticType.value",
            inputProp: ({ field }) => {
                const aquaticSpeciesField = field.columns.find(d => d.dataIndex === "refAquaticSpecies.value")?.search?.field;
                return {
                    list: callAquaticType,
                    renderLabel: ({ item }) => (
                        <div>
                            {item.value} ({item.valueEn})
                        </div>
                    ),
                    "onUpdate:value": value => {
                        field.searchValue = value;
                        if (aquaticSpeciesField) {
                            aquaticSpeciesField.searchValue = null;
                        }
                    }
                };
            },
            search: {
                name: "refAquaticType.id"
            }
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.product.refAquaticSpecies"),
            dataIndex: "refAquaticSpecies.value",
            inputProp: ({ field }) => {
                const aquaticType = field.columns.find(d => d.dataIndex === "refAquaticType.value")?.search?.field?.searchValue;
                return {
                    list: ({ searchQuery }) => callAquaticSpecies({ parentId: aquaticType?.id, searchQuery }),
                    renderLabel: ({ item }) => <div>{item.value}</div>,
                    disabled: !aquaticType
                };
            },
            search: {
                name: "refAquaticSpecies.id"
            }
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.product.refProductMethod"),
            dataIndex: "refProductMethod.value",
            inputProp: () => ({
                list: callProductMethod,
                renderLabel: ({ item }) => (
                    <div>
                        {item.value} ({item.valueEn})
                    </div>
                )
            }),
            search: {
                name: "refProductMethod.id"
            }
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.product.refProductFeture"),
            dataIndex: "refProductFeture.value",
            inputProp: () => ({
                list: callProductFeture,
                renderLabel: ({ item }) => (
                    <div>
                        {item.value} ({item.valueEn})
                    </div>
                )
            }),
            search: {
                name: "refProductFeture.id"
            }
        }),
        createColumnInput({ title: () => i18n.global.t("model.createBy"), dataIndex: "refCreateBy.fullName" }),
        createColumnDatetime({ title: () => i18n.global.t("model.createDate"), dataIndex: "createDate" })
    ];
    return columns;
};

export const createForm = () => {
    return {
        refProductType: null,
        refProductConsumption: null,
        refAquaticSource: null,
        refAquaticType: null,
        refAquaticSpecies: null,
        refProductMethod: null,
        refProductFeture: null,
        code: null,
        nameTh: null,
        nameEn: null,
        remark: null,
        issueDate: null,
        cancelDate: null,
        isMicro: true,
        isChemi: true,
        isPhysic: true,
        refDepartment: null
    };
};

export const createRules = ({ props, data, refs }) => {
    return {
        refProductType: [required],
        refProductConsumption: [required],
        refAquaticSource: [required],
        refAquaticType: [required],
        refAquaticSpecies: [required],
        refProductMethod: [required],
        refProductFeture: [required],
        nameTh: [required],
        nameEn: [required],
        code: [createRuleCode({ props, data, refs })]
    };
};

export const createRuleCode = ({ data }) => {
    return {
        validator: () => {
            const modelRef = data.useForm.modelRef;
            const {
                refProductType,
                refProductConsumption,
                refAquaticSource,
                refAquaticType,
                refAquaticSpecies,
                refProductMethod,
                refProductFeture
            } = modelRef;
            if (
                refProductType &&
                refProductConsumption &&
                refAquaticSource &&
                refAquaticType &&
                refAquaticSpecies &&
                refProductMethod &&
                refProductFeture
            ) {
                const code = calProductCode({ data: modelRef });
                const where = [{ name: "code", type: "String", operator: "=", value: code }];
                if (modelRef.id) {
                    where.push({ name: "id", type: "UUID", operator: "!=", value: modelRef.id });
                }
                return callSearch({ criteria: { select: ["id"], page: 0, size: 1, where } }).then(page => {
                    if (page.totalSize > 0) {
                        return Promise.reject(i18n.global.t("error.product.codeDup"));
                    }
                    return Promise.resolve();
                });
            }
            return Promise.resolve();
        },
        required: true,
        trigger: "change"
    };
};

export const createField = ({ props, data }) => {
    const viewOnly = props.viewOnly;
    const useForm = data.useForm;
    const modelRef = useForm.modelRef;
    return [
        createFieldInput({
            key: `code`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.product.code"),
            inputProp: () => {
                let code = calProductCode({ data: modelRef });
                return { value: code, disabled: true };
            }
        }),
        createFieldSelect({
            key: `refProductType`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.product.refProductType"),
            inputProp: () => ({
                value: modelRef.refProductType,
                "onUpdate:value": value => {
                    modelRef.refProductType = value;
                    calProductName({ data });
                },
                disabled: viewOnly,
                list: callProductType,
                renderLabel: ({ item }) => (
                    <div>
                        {item.value} ({item.valueEn})
                    </div>
                )
            }),
            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
        }),
        createFieldSelect({
            key: `refProductConsumption`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.product.refProductConsumption"),
            inputProp: () => ({
                value: modelRef.refProductConsumption,
                "onUpdate:value": value => {
                    modelRef.refProductConsumption = value;
                    calProductName({ data });
                },
                disabled: viewOnly,
                list: callProductConsumption,
                renderLabel: ({ item }) => (
                    <div>
                        {item.value} ({item.valueEn})
                    </div>
                )
            }),
            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
        }),
        createFieldSelect({
            key: `refAquaticSource`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.product.refAquaticSource"),
            inputProp: () => ({
                value: modelRef.refAquaticSource,
                "onUpdate:value": value => {
                    modelRef.refAquaticSource = value;
                    calProductName({ data });
                },
                disabled: viewOnly,
                list: callAquaticSource,
                renderLabel: ({ item }) => (
                    <div>
                        {item.value} ({item.valueEn})
                    </div>
                )
            }),
            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
        }),
        createFieldSelect({
            key: `refAquaticType`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.product.refAquaticType"),
            inputProp: () => ({
                value: modelRef.refAquaticType,
                "onUpdate:value": value => {
                    modelRef.refAquaticType = value;
                    modelRef.refAquaticSpecies = null;
                    calProductName({ data });
                },
                disabled: viewOnly,
                list: callAquaticType,
                renderLabel: ({ item }) => (
                    <div>
                        {item.value} ({item.valueEn})
                    </div>
                )
            }),
            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
        }),
        createFieldSelect({
            key: `refAquaticSpecies`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.product.refAquaticSpecies"),
            inputProp: () => ({
                value: modelRef.refAquaticSpecies,
                "onUpdate:value": value => {
                    modelRef.refAquaticSpecies = value;
                    calProductName({ data });
                },
                disabled: viewOnly || !modelRef.refAquaticType,
                list: ({ searchQuery }) => callAquaticSpecies({ parentId: modelRef.refAquaticType?.id, searchQuery }),
                renderLabel: ({ item }) => <div>{item.value}</div>
            }),
            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
        }),
        createFieldSelect({
            key: `refProductMethod`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.product.refProductMethod"),
            inputProp: () => ({
                value: modelRef.refProductMethod,
                "onUpdate:value": value => {
                    modelRef.refProductMethod = value;
                    calProductName({ data });
                },
                disabled: viewOnly,
                list: callProductMethod,
                renderLabel: ({ item }) => (
                    <div>
                        {item.value} ({item.valueEn})
                    </div>
                )
            }),
            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
        }),
        createFieldSelect({
            key: `refProductFeture`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.product.refProductFeture"),
            inputProp: () => ({
                value: modelRef.refProductFeture,
                "onUpdate:value": value => {
                    modelRef.refProductFeture = value;
                    calProductName({ data });
                },
                disabled: viewOnly,
                list: callProductFeture,
                renderLabel: ({ item }) => (
                    <div>
                        {item.value} ({item.valueEn})
                    </div>
                )
            }),
            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
        }),
        createFieldInput({
            key: "nameTh",
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.product.nameTh"),
            inputProp: () => ({ value: modelRef.nameTh, "onUpdate:value": value => (modelRef.nameTh = value), disabled: viewOnly })
        }),
        createFieldInput({
            key: "nameEn",
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.product.nameEn"),
            inputProp: () => ({ value: modelRef.nameEn, "onUpdate:value": value => (modelRef.nameEn = value), disabled: viewOnly })
        }),
        createFieldDate({
            key: "issueDate",
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.product.issueDate"),
            inputProp: () => ({ value: modelRef.issueDate, "onUpdate:value": value => (modelRef.issueDate = value), disabled: viewOnly })
        }),
        createFieldDate({
            key: "cancelDate",
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.product.cancelDate"),
            inputProp: () => ({ value: modelRef.cancelDate, "onUpdate:value": value => (modelRef.cancelDate = value), disabled: viewOnly })
        }),
        createFieldTreeSelect({
            key: "refDepartment",
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.product.refDepartment"),
            inputProp: () => ({
                value: modelRef.refDepartment,
                "onUpdate:value": value => (modelRef.refDepartment = value),
                disabled: viewOnly,
                list: () => store.dispatch("resource/v1/master/department/cacheFindAllWithChildren"),
                optLabel: "nameTh"
            })
        }),
        createFieldTemplateBuilder({
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => {
                return <span>{i18n.global.t("model.product.labType")}</span>;
            },
            inputProp: () => ({
                useForm: useForm,
                fields: putField({
                    data,
                    fields: [
                        createFieldCheckbox({
                            key: "isMicro",
                            colProp: () => ({ xs: 24, md: 12 }),
                            formItemProp: () => ({ wrapperCol: { xs: 24, md: 18 } }),
                            inputProp: () => ({
                                text: () => i18n.global.t("model.product.isMicro"),
                                checked: modelRef.isMicro,
                                "onUpdate:checked": checked => (modelRef.isMicro = checked),
                                disabled: viewOnly
                            })
                        }),
                        createFieldCheckbox({
                            key: "isChemi",
                            colProp: () => ({ xs: 24, md: 16 }),
                            formItemProp: () => ({ wrapperCol: { xs: 24, md: 18 } }),
                            inputProp: () => ({
                                text: () => i18n.global.t("model.product.isChemi"),
                                checked: modelRef.isChemi,
                                "onUpdate:checked": checked => (modelRef.isChemi = checked),
                                disabled: viewOnly
                            })
                        }),
                        createFieldCheckbox({
                            key: "isPhysic",
                            colProp: () => ({ xs: 24, md: 16 }),
                            formItemProp: () => ({ wrapperCol: { xs: 24, md: 18 } }),
                            inputProp: () => ({
                                text: () => i18n.global.t("model.product.isPhysic"),
                                checked: modelRef.isPhysic,
                                "onUpdate:checked": checked => (modelRef.isPhysic = checked),
                                disabled: viewOnly
                            })
                        })
                    ]
                })
            })
        })
    ];
};

export const createFormTitle = ({ data }) => {
    return data.useForm.modelRef.id ? data.useForm.modelRef.nameTh : i18n.global.t("button.create");
};

export const loadForm = ({ props, data }) => {
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ id }) });
    } else {
        promise = Promise.resolve({ data: [modelRef] });
    }
    return promise
        .then(page => initUseForm({ data, page, routerPrefix }))
        .then(() => {
            if (!id) {
                modelRef.refDepartment = store.state.resource.auth.info.user?.refDepartment;
            }
        })
        .finally(() => setIsLoading(false));
};

export const initState = ({ props }) => {
    const menuContext = props.menuContext;
    state.manages = [
        createManageSelect({
            menuContext,
            getCheckboxProps: ({ record }) => ({ disabled: record.isActive !== true || window.selectGetCheckboxPropDisabled?.({ record }) })
        }),
        createManageActive({
            menuContext,
            onClick: ({ rowSelection }) => onClickActiveAll({ rowSelection, callActiveAll, state, onSearch, setIsLoading })
        }),
        createManageArchive({
            menuContext,
            onClick: ({ rowSelection }) => onClickArchiveAll({ rowSelection, callArchiveAll, state, onSearch, setIsLoading })
        }),
        createManageDelete({
            menuContext,
            onClick: ({ rowSelection }) => onClickDeleteAll({ rowSelection, callDeleteAll, state, onSearch, setIsLoading })
        }),
        createManageExportCsv({
            menuContext,
            callData: ({ page }) => onSearch({ props, page: { ...lodash.cloneDeep({ ...page, page: 0, size: 1000000 }) }, isSet: false })
        })
    ];
    state.manageExtras = [createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix }) })];
    state.rowActions = [
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        createRowActionEdit({ menuContext, onClick: ({ record }) => onClickEdit({ record, routerPrefix }) })
    ];
};

export const initForm = ({ props, data, refs }) => {
    checkAttribute({ props, data, refs });
    data.form = createForm();
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs });
    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                    default: () => createAppPageHeaderDefault({ props, data, refs })
                }
            });
        }
    });
};

export const createAppPageHeaderDefault = ({ props, data }) => {
    const col1 = [];
    const col2 = [];
    const { menuContext, viewOnly } = props;
    const { id, isActive } = data.useForm.modelRef;
    if (id && has({ menuContext, key: "canCreate" })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && id && has({ menuContext, key: "canView" })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (viewOnly && id && isActive && has({ menuContext, key: "canEdit" })) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && (!id || (id && isActive))) {
        col1.push(createButtonSave({ onClick: () => onClickSave({ data }), classStyle: "m-1" }));
    }
    if (viewOnly && id && isActive && has({ menuContext, key: "canEdit" })) {
        col2.push(
            createButtonCopy({
                onClick: () => {
                    const form = copyForm({ form: data.useForm.modelRef });
                    router.push({
                        name: `${routerPrefix}-create`,
                        params: { props: JSON.stringify({ form }) }
                    });
                },
                classStyle: "m-1"
            })
        );
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: "canActive" })) {
        col2.push(
            createButtonActive({
                onClick: () =>
                    onClickActive({
                        record: { id },
                        callActive,
                        setIsLoading: setIsLoadingApp,
                        onClickEdit: () => onClickEdit({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canArchive" })) {
        col2.push(
            createButtonArchive({
                onClick: () =>
                    onClickArchive({
                        record: { id },
                        callArchive,
                        setIsLoading: setIsLoadingApp,
                        onClickView: () => onClickView({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canDelete" })) {
        col2.push(
            createButtonDelete({
                onClick: () => onClickDelete({ record: { id }, callDelete, setIsLoading: setIsLoadingApp, state, routerPrefix }),
                classStyle: "m-1"
            })
        );
    }
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

const calProductName = ({ data }) => {
    const modelRef = data.useForm.modelRef;
    modelRef.nameTh = [
        modelRef.refProductType?.value,
        modelRef.refProductConsumption?.value,
        modelRef.refAquaticSource?.value,
        modelRef.refAquaticType?.value,
        modelRef.refAquaticSpecies?.value,
        modelRef.refProductMethod?.value,
        modelRef.refProductFeture?.value
    ]
        .filter(d => d)
        .join(" ");
    modelRef.nameEn = [
        modelRef.refProductType?.valueEn,
        modelRef.refProductConsumption?.valueEn,
        modelRef.refAquaticSource?.valueEn,
        modelRef.refAquaticType?.valueEn,
        modelRef.refAquaticSpecies?.valueEn,
        modelRef.refProductMethod?.valueEn,
        modelRef.refProductFeture?.valueEn
    ]
        .filter(d => d)
        .join(" ");
};

const calProductCode = ({ data }) => {
    const { refProductType, refProductConsumption, refAquaticSource, refAquaticType, refAquaticSpecies, refProductMethod, refProductFeture } = data;
    let code = "";
    code += refProductType?.key || " ";
    code += refProductConsumption?.key || " ";
    code += refAquaticSource?.key || " ";
    code += refAquaticType?.key || " ";
    code += refAquaticSpecies?.key || "  ";
    code += refProductMethod?.key || "";
    code += refProductFeture?.key || "  ";
    return code;
};

export const openModalList = ({ bind, onIframeMounted }) => {
    const options = addModal({
        bind: lodash.merge(lodash.cloneDeep(modalStyleIFrame), bind),
        render: () => (
            <CIFrame
                src={`${process.env.VUE_APP_PUBLIC_PATH}/app/master-mgt/products?isLayout=false&canSelect=true&cacheQuery=true`}
                onIframeMounted={({ refs }) => onIframeMounted({ options, refs })}
            ></CIFrame>
        )
    });
    return options;
};

export const state = reactive({
    columns: createColumns(),
    page: createPage({ options: { select: ["**", "refCreateBy.fullName"] } }),
    tableOptions: { search: { activeKey: "Search" } }
});
